import type { Entity } from '../../../core/models/entity.model';
import type { CombinedPropertyDetails } from '../../store/product-view/helpers/multi-edit-product.helpers';
import type { MeasurementUnit } from '../../../core/models/measurement-unit.model';

export interface ToolTipHelperObject {
  value: unknown;
  name: string;
  entity: Entity;
  children: ToolTipHelperObject[];
  measurementUnitTextId?: string;
}

type NameResolver = (entity: Entity) => string;

/**
 * Creates a tree-like structure for displaying the different values in different entities for a property in the multi-product
 * editor interface.
 * @param detail Combined property information object
 * @param entities List of entities, containing at least all the entities properties were combined from
 * @param measurementUnits Measurement unit types, needed for getting text ids
 * @param nameResolver Function which resolves the presentation name of an entity
 * @returns A list of root-level tooltip info objects - essentially there is one for each top-level entity
 */
export const createMultiEditPropertyToolTipObjectTree = (
  detail: CombinedPropertyDetails,
  entities: Entity[],
  measurementUnits: Record<string, MeasurementUnit>,
  nameResolver: NameResolver
): ToolTipHelperObject[] => {
  // Map of all the helper objects, this will allow easy id-based access
  const toolTipObjectMap: Record<string, ToolTipHelperObject> = {};

  // Set of root helper objects (entities with no parents)
  const rootToolTips = new Set<ToolTipHelperObject>();

  // Measurement unit of the property
  const measurementUnit = measurementUnits[detail.measurementUnit];

  /*
   * We loop through the list of different values for the property, each value containing the actual value as well as the
   * id of the product the value came from.
   * For each product we create an entry in the toolTipObjectMap which contains the information about this specific value.
   * We also add the parents of the product to the map, if they do not exist. The child is then pushed into the children-
   * array of the parent. This gives us a tree-style structure of all the products we wish to show in the value listing.
   */
  for (const { value, productId } of detail.values) {
    // Get the current product
    const product = entities.find(e => e.Id === productId);

    // Create an entry for the current product
    toolTipObjectMap[product.Id] = {
      value,
      measurementUnitTextId: measurementUnit?.TextId,
      entity: product,
      children: [],
      name: nameResolver(product)
    };

    // If the product has a parent, call the recursive parent-adding function, then add all the returned root objects to
    // the set.
    if (product.ParentId) {
      insertParentData(product, entities, toolTipObjectMap, nameResolver).forEach(i => rootToolTips.add(i));
    }
  }

  return [...rootToolTips];
};

const insertParentData = (
  child: Entity,
  entities: Entity[],
  toolTipObjectMap: Record<string, ToolTipHelperObject>,
  nameResolver: NameResolver
): ToolTipHelperObject[] => {
  const rootToolTips: ToolTipHelperObject[] = [];

  // Get the parent of the child, then check if there already is a tooltip object and generate one if not.
  const parent = entities.find(e => e.Id === child.ParentId);
  if (!toolTipObjectMap[parent.Id]) {
    toolTipObjectMap[parent.Id] = {
      value: null,
      children: [],
      entity: parent,
      name: nameResolver(parent)
    };
  }

  // Add the child to the parents' children-property
  toolTipObjectMap[parent.Id].children.push(toolTipObjectMap[child.Id]);

  // If the parent also has a parent, call the function recursively. If not, add the current item to the root tooltips.
  if (parent.ParentId) {
    rootToolTips.push(...insertParentData(parent, entities, toolTipObjectMap, nameResolver));
  } else {
    rootToolTips.push(toolTipObjectMap[child.Id]);
  }

  return rootToolTips;
};
